import React, { forwardRef, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import cx from "classnames";
import IBuildrProps from "../../utilities/IBuildrProps";
import { parseHTML } from "../../utilities/markdown";
import { AutomationTestIdFeature, AutomationTestIdElementType, createDataTestId } from "../../utilities/automation";

export interface IHorizontalTextProps extends IBuildrProps {
    text: string,
    buildrClassName: string,
    outlineColor: string,
    onOffsetChange?: (number) => void
}

export default styled(forwardRef(({ className, buildrClassName, editorClassObj, text, cmsId, outlineColor, onOffsetChange = () => {} }: IHorizontalTextProps, ref: any) => {
    const wrapperRef = useRef(null); // used for detecting y position of wrapper when page scrolling
    const parallaxRef = useRef(null); // used for setting x position of text
    const textLength = text ? text.length : 0; 
    const useSmallText = textLength >= 35;
    const useMediumText = textLength >= 25 && textLength < 35;

    const [outlineColorClass, setOutlineColorClass] = useState(outlineColor);

    useEffect(() => {
        setOutlineColorClass(outlineColor);
    }, [outlineColor])

    function handleScroll() {
        // calculate the y position of the component in the viewport in percentage form.
        // (100% when TOP of component is at BOTTOM of viewport, 0% when BOTTOM of component is at TOP of viewport)
        const yPercentInViewPort = (Number(((wrapperRef.current.getBoundingClientRect().top+(wrapperRef.current.clientHeight))/(window.innerHeight+wrapperRef.current.clientHeight)).toFixed(3)));

        // start: left of text at right of viewport, end: right of text at left of viewport
        // parallaxRef.current.style.transform = `translateX(${yPercentInViewPort*(window.innerWidth+parallaxRef.current.clientWidth)-parallaxRef.current.clientWidth}px)`;

        // start: left of text at middle of viewport, end: right of text at middle of viewport
        const translateX = (window.innerWidth/2) - ((1-yPercentInViewPort)*parallaxRef.current.clientWidth);
        parallaxRef.current.style.transform = `translateX(${translateX}px)`;
        onOffsetChange(translateX);
    }

    useEffect(() => {
        // on page load set left of text at right of viewport
        // parallaxRef.current.style.transform = `translateX(${window.innerWidth}px)`;

        // on page load set left of text at middle of viewport width
        parallaxRef.current.style.transform = `translateX(${window.innerWidth/2}px)`;

        const options = {
            threshold: 0,
        };

        const handleIntersection = (entries) => {
            // only one element being observed [0]
            if (entries[0]?.isIntersecting) {
                // only monitor scrolling when element is in viewport
                document.addEventListener('scroll', handleScroll);
            } else {
                document.removeEventListener('scroll', handleScroll);
            }
        };
        const observer = new IntersectionObserver(handleIntersection, options);

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            if (wrapperRef.current) {
                observer.unobserve(wrapperRef.current);
            }
        };
    }, []);

    return (
        <div data-id={cmsId} ref={ref} className={cx(className, buildrClassName, editorClassObj, 'overflow-hidden')}>
            <div ref={wrapperRef}>
                <h2 ref={parallaxRef} data-test-id={createDataTestId({feature: AutomationTestIdFeature.TEXT_PARALLAX, element: AutomationTestIdElementType.CONTAINER, specifier: 'text'})} className={cx('parallax-text text-uppercase d-inline-block text-right m-0 p-0', outlineColorClass, {'medium-parallax-text': useMediumText }, {'small-parallax-text': useSmallText })}>{parseHTML(text, 'HorizontalText')}</h2>
            </div>
        </div>
    );
}))`
.parallax-text {
    font-family: 'National2';
    color: transparent;
    -webkit-text-stroke: 1.5px #000000;
    font-size: 6.5rem !important;
    font-weight: 700 !important;
    transition: transform 0.2s ease-out;
    @media only screen and (max-width: 700px) {
        font-size: 4rem !important;
    }

    &.outline-black {
        -webkit-text-stroke: 1.5px var(--black);
    }

    &.outline-white {
        -webkit-text-stroke: 1.5px var(--white);
    }

    &.outline-redRock {
        -webkit-text-stroke: 1.5px var(--redRock);
    }
}
.medium-parallax-text {
    font-size: 4.5rem !important;
    @media only screen and (max-width: 700px) {
        font-size: 3rem !important;
    }
}
.small-parallax-text {
    font-size: 3.5rem !important;
    @media only screen and (max-width: 700px) {
        font-size: 2.5rem !important;
    }
}
    
`;